/**
 * Guide Tour — type definitions.
 *
 * @see docs/guide-tour/implementation.md
 */

/** Module identifier for logging and future package extraction. */
export const GUIDE_TOUR_MODULE_ID = 'guide-tour' as const

export const GUIDE_TOUR_TYPES = ['feature', 'onboard'] as const

export type GuideTourType = (typeof GUIDE_TOUR_TYPES)[number]

export const GUIDE_TOUR_PLACEMENTS = [
  'top',
  'bottom',
  'left',
  'right',
  'auto',
] as const

export type GuideTourPlacement = (typeof GUIDE_TOUR_PLACEMENTS)[number]

/** Step/tour modal gate: boolean, specific modal id, or unset. */
export type GuideTourModalVisibleGate = boolean | string | undefined

export type GuideTourStep = {
  /**
   * Matches `data-testid`, element `id`, or legacy `data-guide-tour-id` when the step highlights a target.
   * Omit for screen-level explanations (no spotlight).
   */
  targetId?: string
  title: string
  body: string
  /**
   * Route where this step is shown. Inherits `route` from the tour when omitted.
   * Required on every step when the tour has no top-level `route` (multi-page flow).
   */
  route?: string
  /** Optional category label shown on the step card (e.g. "Preferência"). */
  badge?: string
  /** When omitted, the step card renders a text-only layout. */
  imageUrl?: string
  /**
   * Optional card position relative to the highlighted target.
   * Defaults to `auto` (runtime picks the best side based on available space).
   */
  placement?: GuideTourPlacement
  /**
   * When `true`, waits until no modal is open before showing this step.
   * Detects Hexa V2 dialogs, `.backdrop`, legacy modal overlays, etc.
   */
  waitForModalHidden?: boolean
  /**
   * Element ids/classes that must disappear before this step (non-modal UI).
   * Examples: onboarding callouts, banners, skeleton loaders, coach marks.
   * Accepts ids (`header-order-bell-indicator`) or classes (`.persistent-callout`).
   */
  waitForHidden?: string[]
  /**
   * Override modal wait for this step.
   *
   * Omit to use defaults: after a step with `forceClick`, the next step waits for
   * any modal automatically. Set a string to wait for a specific modal id.
   * Set `false` to skip waiting.
   */
  waitForModalVisible?: boolean | string
  /**
   * Element id to click when the user presses Próximo on this step.
   * Opens modals / triggers navigation before advancing (e.g. `"btn-open-map-modal"`).
   */
  forceClick?: string
  /**
   * Optional element id to scroll before showing this step when it differs from `targetId`
   * (e.g. scroll a parent section while highlighting a child). When omitted, the runtime
   * scrolls to `targetId` automatically whenever it exists in the DOM but is off-screen.
   */
  scrollIntoView?: string
  /**
   * When `false`, the tour waits until the target is already visible in the viewport
   * instead of programmatically scrolling to it. Defaults to automatic scroll for
   * steps with `targetId`. Ignored while the route has multiple pending tours.
   */
  autoScrollToTarget?: boolean
  /**
   * When `true`, clicks on the highlighted target reach the page (e.g. the user must
   * interact before advancing). Defaults to `false` — the spotlight is view-only and
   * Próximo / forceClick drive the flow.
   */
  allowTargetInteraction?: boolean
  /**
   * Last month this step may appear (`YYYY-MM`). Hidden from the next month onward.
   * Omit for lifetime steps. Does not inherit the tour-level `expiresAt`.
   */
  expiresAt?: string
  /**
   * Alternate flows when this step's `targetId` is absent from the DOM.
   * Evaluated in order; the first flow whose entry target exists wins.
   */
  whenTargetMissing?: readonly {
    steps: GuideTourStep[]
  }[]
}

export type GuideTourDefinition = {
  id: string
  /**
   * `onboard` — primeira visualização / orientação da plataforma.
   * `feature` — novidade pontual; incremente `version` para reexibir.
   */
  type: GuideTourType
  /**
   * When `false`, the tour is kept in the registry for documentation and validation
   * but never auto-starts. Use while drafting content with the squad.
   */
  enabled?: boolean
  /**
   * Tie-breaker within the same tier (0–9). Higher runs first among peers.
   * Does not cross tiers: feature (40–49) always beats page (10–19).
   * When omitted, defaults to 0.
   */
  priority?: number
  /** Single-page tours: all steps inherit this route unless a step overrides it. */
  route?: string
  /**
   * Groups related tours (e.g. a release touching several pages).
   * Metadata for squads and future analytics — does not change runtime order.
   */
  campaignId?: string
  /**
   * Bump when copy or steps change. Users who saw the previous version see it again.
   */
  version: number
  /**
   * Last month the whole tour may appear (`YYYY-MM`). Hidden from the next month onward.
   * Omit for lifetime tours. Individual steps may also define their own `expiresAt`.
   */
  expiresAt?: string
  /**
   * When `true`, lists this tour in the header notification center even before
   * the seller visits the route. Does not auto-open on route — start from the panel.
   */
  listInNotifications?: boolean
  /**
   * When `true`, waits until no modal is open before auto-activating (step 0).
   * Detects Hexa V2 dialogs, `.backdrop`, legacy modal overlays, etc.
   */
  waitForModalHidden?: boolean
  /**
   * Element ids/classes that must disappear before auto-activation (non-modal UI).
   * Examples: onboarding callouts, banners, skeleton loaders.
   */
  waitForHidden?: string[]
  /**
   * Default for all steps. When `false`, the tour waits until the target is already
   * visible instead of programmatically scrolling. Steps with `targetId` scroll by default.
   */
  autoScrollToTarget?: boolean
  /** Main flow while entry step targets exist in the DOM. */
  steps: GuideTourStep[]
}

export type GuideTourRegistry = {
  getGuideTour: (id: string) => GuideTourDefinition | undefined
  listGuideTours: () => readonly GuideTourDefinition[]
}

export const GUIDE_TOUR_STATUSES = [
  'idle',
  'active',
  'completed',
  'skipped',
] as const

export type GuideTourStatus = (typeof GUIDE_TOUR_STATUSES)[number]

export type GuideTourRuntimeState = {
  status: GuideTourStatus
  activeTourId: string | null
  activeTour: GuideTourDefinition | null
  currentStepIndex: number
  viewedStepCount: number
}

export type GuideTourAction =
  | {
      type: 'START'
      tour: GuideTourDefinition
      stepIndex?: number
      viewedStepCount?: number
    }
  | { type: 'NEXT' }
  | { type: 'PREV' }
  | { type: 'SKIP' }
  | { type: 'DISMISS' }
  | { type: 'COMPLETE' }
  | { type: 'MARK_STEP_VIEWED' }
  | { type: 'GO_TO_STEP'; stepIndex: number }
  | { type: 'RESET' }

export const GUIDE_TOUR_BLOCKED_TARGET_INTERACTION_MESSAGE_LINES = [
  'Você está no tour de novidades.',
  'Os cliques nesta área ficam desativados.',
] as const

export const GUIDE_TOUR_BLOCKED_TARGET_INTERACTION_MESSAGE =
  GUIDE_TOUR_BLOCKED_TARGET_INTERACTION_MESSAGE_LINES.join(' ')

/** How long the danger notice stays visible before auto-hiding. */
export const GUIDE_TOUR_BLOCKED_TARGET_INTERACTION_WARNING_MS = 5_000
